"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useInView } from "motion/react";

type VideoPlayerProps = {
  src: string;
  /** Shown until playback starts, and again once a non-looping video ends. */
  poster: string;
  /** False while the video sits in a tab that is not selected. */
  isActive: boolean;
  loop?: boolean;
  /**
   * "autoplay-muted" starts silently once the video scrolls into view;
   * "click-with-sound" waits for the Play button and starts with audio on,
   * which is the only way a browser lets us unmute.
   */
  startMode?: "autoplay-muted" | "click-with-sound";
  /** Move the controls off whatever the footage keeps in that corner. */
  controlsAlign?: "left" | "right";
};

export const VideoPlayer = ({
  src,
  poster,
  isActive,
  loop = false,
  startMode = "autoplay-muted",
  controlsAlign = "left",
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(videoRef, { margin: "-100px 0px -100px 0px" });
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleAudio = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  };

  const stop = () => {
    videoRef.current?.pause();
  };

  const play = () => {
    const video = videoRef.current;
    if (!video) return;

    if (startMode === "click-with-sound") video.muted = false;
    video.play();
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    // "ended" does not imply "pause", so the poster needs its own trigger.
    const handleEnded = () => setIsPlaying(false);
    const handleVolumeChange = () => setMuted(!!video.muted);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("volumechange", handleVolumeChange);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("volumechange", handleVolumeChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!isActive || !isInView) {
      video.pause();
      return;
    }

    // Scrolling back into view must not restart a video the visitor paused, and
    // must never start one that carries a voiceover on its own.
    if (startMode === "autoplay-muted") video.play();
  }, [isActive, isInView, startMode]);

  return (
    <div className="relative w-full h-full">
      {isPlaying && (
        <div
          className={cn(
            "absolute bottom-0 left-0 w-full h-fit z-20 p-5 gap-5 flex items-center",
            controlsAlign === "right" && "justify-end",
          )}
        >
          <Button
            onClick={stop}
            variant="d-primary"
            className="border-none size-[50px]"
          >
            <Pause size={24} fill="black" />
          </Button>
          <Button
            onClick={toggleAudio}
            variant="d-primary"
            className="border-none size-[50px]"
          >
            {muted ? (
              <VolumeX size={24} fill="black" />
            ) : (
              <Volume2 size={24} fill="black" />
            )}
          </Button>
        </div>
      )}
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full absolute top-0 left-0 object-cover"
        controls={false}
        preload="metadata"
        loop={loop}
        muted
        playsInline
      />

      {!isPlaying && (
        <>
          <Image
            src={poster}
            alt=""
            width={1100}
            height={780}
            className="object-cover absolute top-0 left-0 w-full h-full"
          />
          <div className="absolute flex items-center justify-center w-full h-full bg-[rgba(37,25,3,.56)] top-0 left-0">
            <Button
              onClick={play}
              variant="d-primary"
              className="border-none size-[50px]"
            >
              <Play size={24} fill="black" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
